import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { QuoteApproverApiService } from '../../services/quote-approver-api.service';
import { QuoteApproverDrawerService } from '../../services/quote-approver-drawer.service';
import { QuoteApproverResponse } from '../../models/quote-approver.model';

/**
 * Lista de Responsables de aprobación de cotizaciones (catálogo reducido).
 * Filtros (búsqueda + estado) en cliente; CRUD vía drawer con toasts.
 */
@Component({
  selector: 'app-quote-approver-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
  ],
  templateUrl: './quote-approver-list.component.html',
})
export class QuoteApproverListComponent implements OnInit, OnDestroy {
  items: QuoteApproverResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: QuoteApproverApiService,
    private drawerService: QuoteApproverDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    // includeInactive=true para poder filtrar activos/inactivos en cliente.
    this.api.getApprovers(this.businessId, true).subscribe({
      next: (data) => {
        let result = [...data];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          result = result.filter((i) =>
            (i.userFullName || '').toLowerCase().includes(term) ||
            (i.cargo || '').toLowerCase().includes(term) ||
            (i.email || '').toLowerCase().includes(term)
          );
        }
        if (this.filtroActive !== '') {
          const want = this.filtroActive === 'true';
          result = result.filter((i) => !!i.isActive === want);
        }
        this.items = result;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando responsables';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: QuoteApproverResponse): void {
    this.drawerService.open({ approverId: item.id });
  }

  toggle(item: QuoteApproverResponse): void {
    this.api
      .updateApprover(item.id, this.businessId, {
        userId: item.userId,
        cargo: item.cargo,
        email: item.email,
        approvalLimit: item.approvalLimit,
        isActive: !item.isActive,
      })
      .subscribe({
        next: () => this.cargar(),
        error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'Error al cambiar el estado' }),
      });
  }

  async eliminar(item: QuoteApproverResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar responsable',
      message: `¿Eliminar a "${item.userFullName || item.userEmail}" como responsable de aprobación?`
    });
    if (!ok) return;
    this.api.deleteApprover(item.id, this.businessId).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Responsable de aprobación'); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  trackById = (_: number, item: QuoteApproverResponse) => item.id;
}
